import React, { useState } from "react";

import styled from "styled-components";

import { LgStatusIcon } from "manager/components/app-summary/AppListV2/StatusIcon";
import { ChevronRightGlyph } from "manager/components/vero1/Vero1ReviewPanel/icons";
import { Formula } from "shared/components/vega/ExpressionEngine";
import { findLumenResult } from "shared/components/vega/LumenV2/utils";

import { DIVIDER, HeaderMeta, INK, MUTED } from "./styled";
import { annotationTitle } from "./annotation";

/* Drawing 4.1's Lumen section rows in the same grammar as the VERO1 rows
   above them (no pip: nothing here is human-touched). With `detailed`, rows
   expand to the entry's formula, evaluated formula, and raw result.

   INTENTIONAL DEVIATION from 4.1: the drawing groups rows under category
   headings with one finding each; we render every boolean entry, ungrouped —
   the spec states no curation rule or category mapping. Open with design
   (vero1-deal-review-tab-plan.md §8); do not "fix" to match the drawing
   without that answer. */

const Row = styled.div<{ $last?: boolean }>`
  border-bottom: ${({ $last }) => ($last ? "none" : `1px solid ${DIVIDER}`)};
  padding: 11px 0;
`;

const RowLine = styled.div<{ $expandable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${({ $expandable }) => ($expandable ? "pointer" : "default")};
`;

const RowName = styled.div`
  flex: 1;
  font: 500 13px "Work Sans", sans-serif;
  color: ${INK};
  min-width: 0;
`;

const RowText = styled.p`
  font: 300 11.5px/1.5 "Work Sans", sans-serif;
  color: ${MUTED};
  margin: 2px 0 0 30px;
  max-width: 88ch;
`;

const ChevronHolder = styled.span<{ $open?: boolean }>`
  display: inline-flex;
  color: ${MUTED};
  transition: transform 0.12s ease;
  transform: rotate(${({ $open }) => ($open ? "90deg" : "0deg")});
`;

const DetailPanel = styled.div`
  margin: 8px 0 2px 30px;
  padding: 10px 12px;
  background: #fafafc;
  border: 1px solid ${DIVIDER};
  border-radius: 4px;
  font-size: 11px;

  pre,
  code {
    font-size: 11px !important;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const DetailLabel = styled.div`
  font: 600 10px "Work Sans", sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${MUTED};
  margin: 8px 0 3px;

  &:first-child {
    margin-top: 0;
  }
`;

const DetailResult = styled.code<{ $pass?: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${({ $pass }) => ($pass ? "#1F7A5C" : "#B4325A")};
`;

interface RunHistoryEntry {
  result?: unknown;
  formula?: string;
  evaluatedFormula?: string;
  explanation?: {
    output?: { title?: string; description?: string } | string;
  };
}

const entryTitle = (entry: RunHistoryEntry): string | null => {
  const output = entry.explanation?.output;
  if (output && typeof output === "object" && output.title) {
    return output.title;
  }
  return annotationTitle(entry.formula);
};

const entryDescription = (entry: RunHistoryEntry): string | null => {
  const output = entry.explanation?.output;
  if (typeof output === "string") return output;
  return output?.description ?? null;
};

export const CompactLumenRun: React.FC<{
  lumenRun: unknown;
  detailed?: boolean;
}> = ({ lumenRun, detailed = false }) => {
  const [openKeys, setOpenKeys] = useState<Record<string, boolean>>({});
  const result = findLumenResult(lumenRun);
  const entries: RunHistoryEntry[] = (
    (result?.evaluation?.history ?? []) as RunHistoryEntry[]
  ).filter((entry) => typeof entry?.result === "boolean");
  if (entries.length === 0) {
    return <HeaderMeta>No screening detail on this run.</HeaderMeta>;
  }
  return (
    <div data-testid="compact-lumen-run">
      {entries.map((entry, index) => {
        const title = entryTitle(entry);
        const description = entryDescription(entry);
        const key = entry.formula ?? title ?? String(index);
        const open = detailed && Boolean(openKeys[key]);
        return (
          <Row key={key} $last={index === entries.length - 1}>
            <RowLine
              $expandable={detailed}
              onClick={
                detailed
                  ? () =>
                      setOpenKeys((prev) => ({ ...prev, [key]: !prev[key] }))
                  : undefined
              }
              role={detailed ? "button" : undefined}
              aria-expanded={detailed ? open : undefined}
              data-testid={detailed ? "compact-lumen-row-toggle" : undefined}
            >
              <LgStatusIcon
                status={entry.result ? "pass" : "fail"}
                size={22}
                testId={`compact-lumen-result-${
                  entry.result ? "pass" : "fail"
                }`}
              />
              <RowName>{title ?? "Screening check"}</RowName>
              {detailed && (
                <ChevronHolder $open={open}>
                  <ChevronRightGlyph />
                </ChevronHolder>
              )}
            </RowLine>
            {description && <RowText>{description}</RowText>}
            {open && (
              <DetailPanel data-testid="compact-lumen-detail">
                {entry.formula && (
                  <>
                    <DetailLabel>Formula</DetailLabel>
                    <Formula formula={entry.formula} highlight />
                  </>
                )}
                {entry.evaluatedFormula && (
                  <>
                    <DetailLabel>Evaluated</DetailLabel>
                    <Formula formula={entry.evaluatedFormula} />
                  </>
                )}
                <DetailLabel>Result</DetailLabel>
                <DetailResult $pass={Boolean(entry.result)}>
                  {String(entry.result)}
                </DetailResult>
              </DetailPanel>
            )}
          </Row>
        );
      })}
    </div>
  );
};
